'use client';

import { useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useReactiveAuthTokens } from '@/app/contexts';
import { getUserEmail } from '@/shared/functions';
import { mergeCaseThreatReports, type CaseThreatReport } from '@/shared/threat-report-sharing';
import { useSharedThreatReports } from './useSharedThreatReports';
import { useThreatReportVersions } from './useThreatReportVersions';

function sessionEmail(idToken: string | null | undefined): string {
  if (!idToken) return '';
  try {
    return getUserEmail(jwtDecode(idToken));
  } catch {
    return '';
  }
}

/**
 * The caller's reports and the case's shared reports, keyed by
 * ``chatKey:epochTimeSecs``. The caller's row wins when both lists have it.
 */
export function useCaseThreatReports(caseId: string | undefined | null): {
  reports: CaseThreatReport[];
  isLoading: boolean;
} {
  const { idToken } = useReactiveAuthTokens();
  const own = useThreatReportVersions(caseId);
  const shared = useSharedThreatReports(caseId);
  const email = sessionEmail(idToken);

  const reports = useMemo(
    () => mergeCaseThreatReports(own.data ?? [], shared.data ?? [], email),
    [own.data, shared.data, email],
  );

  return {
    reports,
    isLoading: own.isLoading || shared.isLoading,
  };
}
